import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Bot,
  BookOpen,
  ChevronDown,
  Compass,
  CornerDownRight,
  Gauge,
  Lock,
  MessagesSquare,
  Radio,
  RefreshCw,
  Search,
  Send,
  Trash2,
  UserCog,
  Waypoints,
  Wrench,
  X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Skeleton } from '@/components/atoms/skeleton';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { useContactSearch } from '@/hooks/contacts/useContactSearch';
import { Textarea } from '@/components/atoms/textarea';
import { FlowBuilder } from '@/components/ai/FlowBuilder';
import { IrisAnalyzeDashboard } from '@/components/ai/IrisAnalyzeDashboard';
import { IrisBatchTesting } from '@/components/ai/IrisBatchTesting';
import { IrisConversationsView } from '@/components/ai/IrisConversationsView';
import { IrisCustomAnswers } from '@/components/ai/IrisCustomAnswers';
import { IrisEscalationRules } from '@/components/ai/IrisEscalationRules';
import { IrisGuidance } from '@/components/ai/IrisGuidance';
import { IrisGuide } from '@/components/ai/IrisGuide';
import { IrisKnowledgeSources } from '@/components/ai/IrisKnowledgeSources';
import { IrisOperator } from '@/components/ai/IrisOperator';
import { IrisProcedures } from '@/components/ai/IrisProcedures';
import { IrisRecommendations } from '@/components/ai/IrisRecommendations';
import { IrisReviewQueue } from '@/components/ai/IrisReviewQueue';
import {
  Chip,
  Eyebrow,
  IrisAddButton,
  IrisButton,
  IrisDisclosure,
  IrisEmpty,
  IrisGhostButton,
  IrisIconButton,
  IrisLinkButton,
  IrisMark,
  IrisNote,
  IrisPanel,
  IrisStep,
  IrisToggle,
  Mono,
  MonoChip,
  irisDraftField,
  irisField,
} from '@/components/ai/iris-ui';
import { Link } from '@tanstack/react-router';
import { Fullbleed } from '@/contexts/fullbleed';
import { cn } from '@/lib/utils';
import {
  useAddRoutingRule,
  useAiReceptionists,
  useCreateReceptionist,
  useDeleteReceptionist,
  useDeleteRoutingRule,
  useResumeTestReceptionist,
  useTestReceptionist,
  useUpdateReceptionist,
  type AiReceptionist,
  type ReceptionistChatReply,
} from '@/lib/ai/receptionists';
import {
  KNOWLEDGE_CATEGORIES,
  KNOWLEDGE_CATEGORY_LABELS,
  useCreateKnowledge,
  useDeleteKnowledge,
  useKnowledge,
  useUpdateKnowledge,
  type KnowledgeCategory,
  type KnowledgeEntry,
} from '@/lib/ai/knowledge';
import {
  conversationTracesKey,
  useConversationTraces,
  type TraceSource,
  type TurnTrace,
} from '@/lib/ai/inspection';

export const Route = createFileRoute('/_dashboard/dashboard/settings/chatbot')({
  component: ChatbotStudioPage,
  /**
   * `?tab=` so another screen can send someone straight to the tab that answers
   * their question — the call flow's Iris panel links here for the handoff
   * rules its canvas cannot draw. Unknown values fall back to the overview
   * rather than erroring: a stale bookmark should land somewhere useful.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: IrisTab } => {
    const tab = search.tab;
    return typeof tab === 'string' && IRIS_TABS.some((t) => t.id === tab)
      ? { tab: tab as IrisTab }
      : {};
  },
});

/** Machine name from a human label: lowercase, letters/digits/underscore,
 * must start with a letter (matches the backend `^[a-z][a-z0-9_]*$`). */
function toMachineName(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) return '';
  return /^[a-z]/.test(slug) ? slug : `bot_${slug}`;
}

// ─── Studio structure ─────────────────────────────────────────────────────────

export type IrisTab =
  | 'overview'
  | 'train'
  | 'guidance'
  | 'handoff'
  | 'answers'
  | 'procedures'
  | 'test'
  | 'deploy'
  | 'inbox'
  | 'reviews'
  | 'insights'
  | 'operator';

/** Tabs in the order you work them; `group` opens a new phase in the tab bar. */
const IRIS_TABS: { id: IrisTab; label: string; group?: string }[] = [
  { id: 'overview', label: 'Overview', group: 'Start' },
  { id: 'train', label: 'Train', group: 'Set up' },
  { id: 'guidance', label: 'Guidance' },
  { id: 'handoff', label: 'Handoff' },
  { id: 'answers', label: 'Answers' },
  { id: 'procedures', label: 'Procedures' },
  { id: 'test', label: 'Test', group: 'Ship' },
  { id: 'deploy', label: 'Deploy' },
  { id: 'inbox', label: 'Inbox', group: 'Improve' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'insights', label: 'Insights' },
  { id: 'operator', label: 'Operator' },
];

const PAGES: Record<IrisTab, { title: string; sub: string }> = {
  overview: {
    title: 'How Iris works',
    sub: 'The whole model on one page — read it once, then work from the tabs.',
  },
  train: { title: 'Train', sub: 'The team, the routing rules, and what Iris knows.' },
  guidance: {
    title: 'Guidance',
    sub: 'How Iris behaves. Plain-language rules applied to every single reply.',
  },
  handoff: {
    title: 'Handoff',
    sub: 'Set who Iris rings on a call flow’s Iris step. This screen is for the rules that step cannot draw.',
  },
  answers: {
    title: 'Answers',
    sub: 'Exact wording for specific questions. A match here beats everything else Iris would say.',
  },
  procedures: {
    title: 'Procedures',
    sub: 'Step-by-step playbooks Iris runs when an intent matches.',
  },
  test: {
    title: 'Test',
    sub: 'Talk to Iris as a customer. Open any reply to see why it said that.',
  },
  deploy: {
    title: 'Deploy',
    sub: 'Everything that must be true before real customers reach Iris.',
  },
  inbox: { title: 'Inbox', sub: 'Real customer threads. Open a reply to see what Iris used.' },
  reviews: {
    title: 'Reviews',
    sub: 'Grade conversations so you know whether Iris is actually good.',
  },
  insights: { title: 'Insights', sub: 'How Iris is doing over the last 30 days.' },
  operator: {
    title: 'Operator',
    sub: 'Iris studies its own weak spots and proposes the fix. You accept or reject.',
  },
};

/** The phase a tab belongs to — shown as the eyebrow above the page subtitle. */
function phaseFor(tab: IrisTab): string {
  let phase = '';
  for (const t of IRIS_TABS) {
    if (t.group) phase = t.group;
    if (t.id === tab) return phase;
  }
  return phase;
}

function ChatbotStudioPage() {
  const { data: receptionists = [], isLoading } = useAiReceptionists();
  const { tab: tabFromUrl } = Route.useSearch();
  const [simOpen, setSimOpen] = useState(true);
  const [tab, setTab] = useState<IrisTab>(tabFromUrl ?? 'overview');

  const page = PAGES[tab];
  const enabledCount = receptionists.filter((b) => b.enabled).length;
  const liveChip = isLoading
    ? 'Checking…'
    : receptionists.length === 0
      ? 'No bots yet'
      : `${enabledCount} of ${receptionists.length} bots on`;

  return (
    <>
      <Fullbleed />
      <div className="iris-studio page-enter flex h-full flex-col bg-[var(--iris-paper)] text-[var(--iris-body)]">
        {/* Page head — eyebrow, title, live state, and the always-available
            simulator. The bars stay full-bleed so their rule reaches the edge;
            it's their CONTENT that clears the docked simulator, which is why
            the gutter is on the inner rows and not on the page column. */}
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-start justify-between gap-5 border-b border-[var(--iris-line)] bg-[var(--iris-surface)] px-7 pb-4 pt-[18px]',
            simOpen && 'xl:pr-[var(--iris-sim-gutter)]',
          )}
        >
          <div className="min-w-0">
            <div className="font-mono text-[10.5px] uppercase leading-none tracking-[0.16em] text-[var(--iris-faint)]">
              Settings · AI · Iris
            </div>
            <h1 className="mt-1.5 text-[26px] font-medium tracking-[-0.03em] text-[var(--iris-ink)]">
              {page.title}
            </h1>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-2 rounded-lg border border-[var(--iris-rule-strong)] px-3 py-2 text-[13.5px] text-[var(--iris-ink)]">
              <Radio className="size-[15px] text-[var(--iris-muted)]" />
              {liveChip}
            </div>
            <IrisButton onClick={() => setSimOpen(true)}>
              <MessagesSquare className="size-[15px]" />
              Test Iris
            </IrisButton>
          </div>
        </div>

        {/* Phase-grouped tab bar. */}
        <div
          className={cn(
            'shrink-0 overflow-x-auto border-b border-[var(--iris-line)] bg-[var(--iris-surface)] px-7',
            simOpen && 'xl:pr-[var(--iris-sim-gutter)]',
          )}
        >
          <div className="flex items-center gap-5" role="tablist" aria-label="Iris studio">
            {IRIS_TABS.map((t) => {
              const active = tab === t.id;
              return (
                <div key={t.id} className="flex shrink-0 items-center gap-5">
                  {t.group ? (
                    <span className="select-none font-mono text-[9.5px] uppercase tracking-[0.16em] text-[var(--iris-ghost)]">
                      {t.group}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.id)}
                    className={cn(
                      'whitespace-nowrap border-b-2 py-3 text-[13.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--iris-ink)]',
                      active
                        ? 'border-[var(--iris-ink)] font-medium text-[var(--iris-ink)]'
                        : 'border-transparent text-[var(--iris-muted)] hover:text-[var(--iris-ink)]',
                    )}
                  >
                    {t.label}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div
            className={cn(
              'w-full max-w-[1220px] px-7 pb-20 pt-[22px]',
              simOpen && 'xl:pr-[var(--iris-sim-gutter)]',
            )}
          >
            {/* Phase + subtitle sit above every tab. */}
            <div className="mb-[18px]">
              <Eyebrow className="text-[10.5px] tracking-[0.16em] text-[var(--iris-faint)]">
                {phaseFor(tab)}
              </Eyebrow>
              <p className="mt-2 max-w-[660px] text-sm leading-relaxed text-[var(--iris-body)]">
                {page.sub}
              </p>
            </div>

            {tab === 'overview' ? (
              <IrisGuide onGoTo={setTab} onOpenSimulator={() => setSimOpen(true)} />
            ) : tab === 'guidance' ? (
              <IrisGuidance />
            ) : tab === 'handoff' ? (
              <IrisEscalationRules />
            ) : tab === 'answers' ? (
              <IrisCustomAnswers />
            ) : tab === 'procedures' ? (
              <IrisProcedures />
            ) : tab === 'reviews' ? (
              <IrisReviewQueue />
            ) : tab === 'inbox' ? (
              <IrisConversationsView />
            ) : tab === 'operator' ? (
              <IrisOperator />
            ) : tab === 'insights' ? (
              <div className="space-y-3">
                <IrisAnalyzeDashboard />
                <IrisRecommendations />
              </div>
            ) : tab === 'deploy' ? (
              <DeployTab bots={receptionists} />
            ) : tab === 'test' ? (
              <TestTab
                hasBots={receptionists.length > 0}
                onOpenSimulator={() => setSimOpen(true)}
              />
            ) : isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-14 w-full rounded-xl" />
                <Skeleton className="h-14 w-full rounded-xl" />
                <Skeleton className="h-14 w-full rounded-xl" />
              </div>
            ) : (
              <TrainTab bots={receptionists} onGoTo={setTab} />
            )}
          </div>
        </div>
      </div>

      {/* Floating customer simulator — available across the whole studio. */}
      {simOpen ? (
        <SimulatorPanel hasBots={receptionists.length > 0} onClose={() => setSimOpen(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setSimOpen(true)}
          className="iris-studio fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-lg bg-[var(--iris-ink)] px-3.5 py-2.5 text-[13px] font-medium text-[var(--iris-on-ink)] shadow-lg hover:bg-[var(--iris-ink-hover)]"
        >
          <MessagesSquare className="size-4" />
          Test Iris
        </button>
      )}
    </>
  );
}

// ─── Train ────────────────────────────────────────────────────────────────────

/**
 * Setup as a numbered spine: team → routing → instructions → knowledge. Each
 * step reports its own summary and count while collapsed, so the closed stack
 * doubles as the setup checklist.
 */
function TrainTab({ bots, onGoTo }: { bots: AiReceptionist[]; onGoTo: (t: IrisTab) => void }) {
  const { data: facts = [] } = useKnowledge();
  const [open, setOpen] = useState<Record<number, boolean>>({ 1: true, 2: true });
  const toggle = (n: number) => setOpen((o) => ({ ...o, [n]: !o[n] }));

  const entry = bots.find((b) => b.isEntrypoint) ?? null;
  const specialists = bots.filter((b) => !b.isEntrypoint);
  const rules = entry?.routingRules ?? [];
  const enabledFacts = facts.filter((f) => f.enabled).length;

  const readyLine = !entry
    ? 'Set an entry point before Iris can answer anything.'
    : rules.length === 0
      ? `${entry.displayName} answers everything itself — add a routing rule to hand work to a specialist.`
      : `${entry.displayName} greets every customer and hands off on ${rules.length} rule${rules.length === 1 ? '' : 's'}.`;

  return (
    <div className="flex flex-col gap-3">
      <IrisStep
        n="01"
        title="Team"
        summary={
          entry
            ? `${entry.displayName} greets everyone${
                specialists.length
                  ? `, then hands off to ${specialists.length} specialist${specialists.length === 1 ? '' : 's'}`
                  : ''
              }.`
            : 'No entry point yet — pick the bot that greets every customer.'
        }
        chip={`${bots.length} bot${bots.length === 1 ? '' : 's'}`}
        open={!!open[1]}
        onToggle={() => toggle(1)}
      >
        <TeamStep entry={entry} specialists={specialists} allBots={bots} />
      </IrisStep>

      <IrisStep
        n="02"
        title="Routing"
        summary={
          entry
            ? 'The first rule that matches wins — everything under it is skipped.'
            : 'Routing lives on the entry-point bot.'
        }
        chip={`${rules.length} rule${rules.length === 1 ? '' : 's'}`}
        open={!!open[2]}
        onToggle={() => toggle(2)}
      >
        <RoutingStep entry={entry} allBots={bots} />
      </IrisStep>

      <IrisStep
        n="03"
        title="Instructions"
        summary="Starts from the Notifi template. Edit the parts that are yours."
        chip={`${bots.length} bot${bots.length === 1 ? '' : 's'}`}
        open={!!open[3]}
        onToggle={() => toggle(3)}
      >
        <InstructionsStep bots={bots} />
      </IrisStep>

      <IrisStep
        n="04"
        title="Knowledge"
        summary="Facts Iris can quote, and long documents it searches at answer time."
        chip={`${enabledFacts} fact${enabledFacts === 1 ? '' : 's'}`}
        open={!!open[4]}
        onToggle={() => toggle(4)}
      >
        <KnowledgeStep />
      </IrisStep>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[var(--iris-hairline)] bg-[var(--iris-surface)] px-4 py-3">
        <p className="text-[13px] text-[var(--iris-body)]">{readyLine}</p>
        <IrisButton className="px-3.5 py-2" onClick={() => onGoTo('deploy')}>
          Deploy <span aria-hidden>→</span>
        </IrisButton>
      </div>
    </div>
  );
}

/** 01 — the roster: who greets, who they hand off to, and who is switched on. */
/** Exported for the layout regression test in `chatbot.team-step.test.tsx`. */
export function TeamStep({
  entry,
  specialists,
  allBots,
}: {
  entry: AiReceptionist | null;
  specialists: AiReceptionist[];
  allBots: AiReceptionist[];
}) {
  const [composing, setComposing] = useState(false);

  return (
    <div className="px-[18px] pb-5 pt-3">
      {entry ? (
        /* Two columns, not a grid: the entry card on the left and one row per
           destination on the right. A grid with `row-span-full` on the entry
           card looked right with a single specialist and collapsed with two —
           `row-span-full` resolves against the EXPLICIT grid, and there are no
           explicit rows here, so it spanned one row and pushed every later
           item into the wrong column. */
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-stretch lg:gap-0">
          <div className="rounded-lg border border-[var(--iris-rule-strong)] p-3.5 lg:w-[254px] lg:shrink-0 lg:self-center">
            <span className="inline-block rounded-full bg-[var(--iris-line)] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--iris-ink)]">
              Entry point
            </span>
            <p className="mt-2.5 text-[15px] font-medium tracking-[-0.01em] text-[var(--iris-ink)]">
              {entry.displayName}
            </p>
            <Mono className="mt-0.5 block">{entry.name}</Mono>
            <p className="mt-2 text-xs leading-relaxed text-[var(--iris-muted)]">
              Greets everyone. Answers what it can, hands off when a rule matches.
            </p>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            {specialists.length === 0 ? (
              <DestinationRow faint>
                No specialists yet — {entry.displayName} handles every conversation itself.
              </DestinationRow>
            ) : (
              specialists.map((b) => (
                <SpecialistRow key={b.id} bot={b} entry={entry} allBots={allBots} />
              ))
            )}

            <DestinationRow faint>No match — {entry.displayName} keeps it.</DestinationRow>
          </div>
        </div>
      ) : (
        /* No entry point yet. The roster still has to be reachable here —
           otherwise a tenant whose bots all happen to be specialists has no
           way to promote one without going hunting through step 03. */
        <>
          <IrisEmpty title="No entry point set">
            One bot has to greet every new customer.
            {allBots.length > 0
              ? ' Promote one of these, or add another.'
              : ' Create one below and it becomes the entry point.'}
          </IrisEmpty>
          {allBots.length > 0 ? (
            <div className="mt-3 flex flex-col gap-1.5">
              {allBots.map((b) => (
                <UnassignedBotRow key={b.id} bot={b} />
              ))}
            </div>
          ) : null}
        </>
      )}

      {composing ? (
        <NewBotComposer
          existingNames={allBots.map((b) => b.name)}
          /* The first bot a tenant creates is the one that greets people. */
          makeEntrypoint={!entry}
          onClose={() => setComposing(false)}
        />
      ) : (
        <IrisAddButton className="mt-3" onClick={() => setComposing(true)}>
          {entry ? 'Add a specialist' : 'Add a bot'}
        </IrisAddButton>
      )}
    </div>
  );
}

/**
 * A bot on a tenant that has no entry point yet — the only thing worth doing
 * to it here is promoting it, so that is the whole row.
 */
function UnassignedBotRow({ bot }: { bot: AiReceptionist }) {
  const update = useUpdateReceptionist();
  return (
    <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-[var(--iris-rule)] bg-[var(--iris-surface)] px-3 py-2.5">
      <span className="text-sm font-medium text-[var(--iris-ink)]">{bot.displayName}</span>
      <Mono>{bot.name}</Mono>
      <IrisGhostButton
        className="ml-auto px-2.5 py-1.5 text-xs"
        disabled={update.isPending}
        onClick={() =>
          update.mutate(
            { id: bot.id, isEntrypoint: true },
            {
              onSuccess: () => toast.success(`“${bot.displayName}” is now the entry point`),
              onError: (err) =>
                toast.error((err as { message?: string })?.message ?? 'Could not update bot'),
            },
          )
        }
      >
        Make entry point
      </IrisGhostButton>
    </div>
  );
}

/**
 * One destination hanging off the entry point: a dashed lead-in on wide
 * screens, then the thing being routed to. The lead-in is decorative and
 * disappears below `lg`, where the column stacks.
 */
function DestinationRow({ faint, children }: { faint?: boolean; children: ReactNode }) {
  return (
    /* `data-destination` is the hook the Team-step layout test counts rows by —
       one per destination, which is what the old grid got wrong. */
    <div className="flex items-center" data-destination="">
      <div className="hidden w-[64px] shrink-0 items-center lg:flex" aria-hidden>
        <div
          className={cn(
            'flex-1 border-t border-dashed',
            faint ? 'border-[var(--iris-rule)]' : 'border-[var(--iris-rule-strong)]',
          )}
        />
        {faint ? (
          <CornerDownRight className="size-3.5 text-[var(--iris-ghost)]" />
        ) : (
          <span className="-ml-1 text-[var(--iris-muted)]">›</span>
        )}
      </div>
      <div
        className={cn(
          'min-w-0 flex-1',
          faint &&
            'rounded-lg border border-dashed border-[var(--iris-rule-strong)] px-3 py-2.5 text-xs text-[var(--iris-muted)]',
        )}
      >
        {children}
      </div>
    </div>
  );
}

function SpecialistRow({
  bot,
  entry,
  allBots,
}: {
  bot: AiReceptionist;
  entry: AiReceptionist;
  allBots: AiReceptionist[];
}) {
  const update = useUpdateReceptionist();
  const del = useDeleteReceptionist();
  const onError = (err: unknown) =>
    toast.error((err as { message?: string })?.message ?? 'Could not update bot');

  /** Phrases that land here — the intents routed to this bot from the entry point. */
  const phrases = entry.routingRules
    .filter((r) => r.targetRoleId === bot.id)
    .map((r) => r.intentPattern);

  const remove = () => {
    if (!window.confirm(`Delete bot “${bot.displayName}”?`)) return;
    del.mutate(bot.id, {
      onSuccess: () => toast.success(`Bot “${bot.displayName}” deleted`),
      onError,
    });
  };

  return (
    <DestinationRow>
      <div
        className={cn(
          'rounded-lg border p-3',
          bot.enabled
            ? 'border-[var(--iris-rule)] bg-[var(--iris-surface)]'
            : 'border-dashed border-[var(--iris-rule)] bg-[var(--iris-hover)]',
        )}
      >
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-2.5 gap-y-1.5">
          <div className="flex min-w-0 flex-wrap items-baseline gap-2.5">
            <span className="text-sm font-medium text-[var(--iris-ink)]">{bot.displayName}</span>
            <Mono>{bot.name}</Mono>
            {allBots.length > 1 ? (
              <IrisLinkButton
                onClick={() =>
                  update.mutate(
                    { id: bot.id, isEntrypoint: true },
                    {
                      onSuccess: () => toast.success(`“${bot.displayName}” is now the entry point`),
                      onError,
                    },
                  )
                }
              >
                Make entry point
              </IrisLinkButton>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <IrisToggle
              checked={bot.enabled}
              label={`${bot.displayName} enabled`}
              onChange={(on) => update.mutate({ id: bot.id, enabled: on }, { onError })}
            />
            <IrisIconButton
              onClick={remove}
              disabled={del.isPending}
              aria-label={`Delete ${bot.displayName}`}
            >
              <Trash2 className="size-3.5" />
            </IrisIconButton>
          </div>
        </div>
        {phrases.length > 0 ? (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {phrases.map((p, i) => (
              /* The ellipsis has to live on a block inside the chip: `Chip` is
                 an inline-flex box, and `text-overflow` does not apply to a
                 flex container — putting `truncate` on the chip itself clips
                 the phrase mid-word with no "…" to show it was cut. */
              <Chip key={i} tone="soft" className="max-w-[260px]" title={p}>
                <span className="min-w-0 truncate">{p}</span>
              </Chip>
            ))}
          </div>
        ) : (
          <p className="mt-2.5 text-xs text-[var(--iris-muted)]">
            Nothing routes here yet — add a rule in step 02.
          </p>
        )}
      </div>
    </DestinationRow>
  );
}

function NewBotComposer({
  existingNames,
  makeEntrypoint = false,
  onClose,
}: {
  existingNames: string[];
  /** Promote this bot on create — used when the tenant has no entry point yet. */
  makeEntrypoint?: boolean;
  onClose: () => void;
}) {
  const create = useCreateReceptionist();
  const [displayName, setDisplayName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');

  const machineName = toMachineName(displayName);
  const nameTaken = machineName.length > 0 && existingNames.includes(machineName);
  const canSubmit = displayName.trim().length > 0 && machineName.length > 0 && !nameTaken;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        name: machineName,
        displayName: displayName.trim(),
        systemPrompt: systemPrompt.trim() || undefined,
        enabled: true,
        ...(makeEntrypoint ? { isEntrypoint: true } : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            makeEntrypoint
              ? `“${displayName.trim()}” created — it greets every customer`
              : `Bot “${displayName.trim()}” created`,
          );
          onClose();
        },
        onError: (err) =>
          toast.error((err as { message?: string })?.message ?? 'Could not create bot'),
      },
    );
  };

  return (
    <div className="mt-3 rounded-lg border border-dashed border-[var(--iris-rule-strong)] bg-[var(--iris-surface)] p-3.5">
      <Eyebrow>{makeEntrypoint ? 'New entry point' : 'New specialist'}</Eyebrow>
      <div className="mt-2.5 space-y-2.5">
        <div>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={makeEntrypoint ? 'e.g. Front Desk' : 'e.g. Scheduler'}
            maxLength={80}
            aria-label="Bot name"
            className={irisField}
          />
          {machineName ? (
            <p className="mt-1.5 text-[11px] text-[var(--iris-muted)]">
              id: <span className="font-mono">{machineName}</span>
              {nameTaken ? (
                <span className="ml-1 text-[var(--iris-warn-fg)]">— already exists</span>
              ) : null}
            </p>
          ) : null}
        </div>
        <textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="What this bot handles, and how it should sound. Leave blank to start from the platform template."
          rows={3}
          maxLength={8000}
          aria-label="Instructions"
          className={cn(irisField, 'leading-relaxed')}
        />
        <div className="flex items-center justify-end gap-3">
          <IrisLinkButton onClick={onClose}>Cancel</IrisLinkButton>
          <IrisButton onClick={submit} disabled={!canSubmit || create.isPending}>
            Create bot
          </IrisButton>
        </div>
      </div>
    </div>
  );
}

/** 02 — priority-ordered handoffs, read top to bottom. */
function RoutingStep({
  entry,
  allBots,
}: {
  entry: AiReceptionist | null;
  allBots: AiReceptionist[];
}) {
  const add = useAddRoutingRule();
  const del = useDeleteRoutingRule();
  const [phrase, setPhrase] = useState('');
  const [target, setTarget] = useState('');

  const onError = (err: unknown) =>
    toast.error((err as { message?: string })?.message ?? 'Could not update routing rules');

  if (!entry) {
    return (
      <div className="px-[18px] pb-5 pt-3.5">
        <IrisEmpty title="No entry point yet">
          Routing rules belong to the bot that greets every customer. Set one in step 01.
        </IrisEmpty>
      </div>
    );
  }

  const others = allBots.filter((b) => b.id !== entry.id);
  const rules = [...entry.routingRules].sort((a, b) => a.priority - b.priority);
  const botById = new Map(allBots.map((b) => [b.id, b]));
  const canAdd = phrase.trim().length > 0 && target.length > 0 && !add.isPending;

  const submit = () => {
    if (!canAdd) return;
    add.mutate(
      { roleId: entry.id, intentPattern: phrase.trim(), targetRoleId: target },
      {
        onSuccess: () => {
          setPhrase('');
          setTarget('');
        },
        onError,
      },
    );
  };

  return (
    <div className="px-[18px] pb-5 pt-3.5">
      <p className="mb-3 text-xs text-[var(--iris-muted)]">
        Read top to bottom. The first rule that matches wins — everything under it is skipped for
        that message.
      </p>

      {others.length === 0 ? (
        <IrisEmpty title="Nowhere to route yet">
          Create a specialist in step 01 and it becomes available here.
        </IrisEmpty>
      ) : (
        <>
          {rules.length > 0 ? (
            <>
              <div className="grid grid-cols-[36px_minmax(0,1fr)_22px_168px_32px] items-center gap-2 px-0.5 pb-2">
                <Eyebrow>Pri</Eyebrow>
                <Eyebrow>When the customer…</Eyebrow>
                <span />
                <Eyebrow>Hand off to</Eyebrow>
                <span />
              </div>

              <div className="flex flex-col gap-1.5">
                {rules.map((r, i) => {
                  const targetBot = r.targetRoleId ? botById.get(r.targetRoleId) : null;
                  const offWarning =
                    targetBot && !targetBot.enabled
                      ? `${targetBot.displayName} is switched off — this rule will not fire.`
                      : null;
                  return (
                    <div
                      key={r.id}
                      className="rounded-lg border border-[var(--iris-rule)] bg-[var(--iris-surface)] px-2 py-2"
                    >
                      <div className="grid grid-cols-[36px_minmax(0,1fr)_22px_168px_32px] items-center gap-2">
                        <span className="pl-1.5 font-mono text-xs text-[var(--iris-ink)]">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span
                          className="min-w-0 truncate text-[13px] text-[var(--iris-ink)]"
                          title={r.intentPattern}
                        >
                          {r.intentPattern}
                        </span>
                        <span className="grid place-items-center text-[13px] text-[var(--iris-muted)]">
                          →
                        </span>
                        <span className="min-w-0 truncate text-[13px] text-[var(--iris-ink)]">
                          {targetBot ? targetBot.displayName : entry.displayName}
                        </span>
                        <IrisIconButton
                          onClick={() =>
                            del.mutate({ roleId: entry.id, ruleId: r.id }, { onError })
                          }
                          aria-label={`Remove rule ${r.intentPattern}`}
                        >
                          <Trash2 className="size-3.5" />
                        </IrisIconButton>
                      </div>
                      {offWarning ? (
                        <IrisNote tone="warn" className="ml-11 mt-2">
                          {offWarning}
                        </IrisNote>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-xs text-[var(--iris-muted)]">
              No rules yet — {entry.displayName} answers everything itself.
            </p>
          )}

          <div className="mt-2.5 grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_168px_62px]">
            <input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="wants to talk to a human"
              maxLength={300}
              aria-label="When the customer…"
              className={irisDraftField}
            />
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              aria-label="Hand off to"
              className={cn(irisDraftField, 'cursor-pointer')}
            >
              <option value="">Hand off to…</option>
              {others.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.displayName}
                </option>
              ))}
            </select>
            <IrisButton className="justify-center px-3 py-2" onClick={submit} disabled={!canAdd}>
              Add
            </IrisButton>
          </div>
        </>
      )}
    </div>
  );
}

/** 03 — per-bot instructions, welcome message, and a live SMS preview. */
function InstructionsStep({ bots }: { bots: AiReceptionist[] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = bots.find((b) => b.id === selectedId) ?? bots[0] ?? null;

  if (!selected) {
    return (
      <div className="px-[18px] pb-5 pt-3.5">
        <IrisEmpty title="No bots yet">
          Create one in step 01 and its instructions land here.
        </IrisEmpty>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 px-[18px] pt-3">
        {bots.map((b) => {
          const active = b.id === selected.id;
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => setSelectedId(b.id)}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-1',
                active
                  ? 'bg-[var(--iris-sunk)] font-medium text-[var(--iris-ink)]'
                  : 'text-[var(--iris-muted)] hover:text-[var(--iris-ink)]',
              )}
            >
              <span
                className={cn(
                  'size-1.5 rounded-full',
                  b.enabled ? 'bg-[var(--iris-ink)]' : 'bg-[var(--iris-ghost)]',
                )}
              />
              {b.displayName}
            </button>
          );
        })}
      </div>
      <BotInstructions key={selected.id} bot={selected} allBots={bots} />
    </div>
  );
}

function BotInstructions({ bot, allBots }: { bot: AiReceptionist; allBots: AiReceptionist[] }) {
  const update = useUpdateReceptionist();
  const [displayName, setDisplayName] = useState(bot.displayName);
  const [prompt, setPrompt] = useState(bot.systemPrompt ?? '');
  const [welcome, setWelcome] = useState(bot.welcomeMessage ?? '');

  const onError = (err: unknown) =>
    toast.error((err as { message?: string })?.message ?? 'Could not update bot');
  const onSaved = () => toast.success('Saved');

  const isFlow = bot.mode === 'FLOW';

  const commitDisplayName = () => {
    const trimmed = displayName.trim();
    if (!trimmed || trimmed === bot.displayName) {
      setDisplayName(bot.displayName);
      return;
    }
    update.mutate({ id: bot.id, displayName: trimmed }, { onSuccess: onSaved, onError });
  };

  const commitPrompt = () => {
    const next = prompt.trim();
    if (next === (bot.systemPrompt ?? '')) return;
    update.mutate({ id: bot.id, systemPrompt: next }, { onSuccess: onSaved, onError });
  };

  const commitWelcome = () => {
    const next = welcome.trim();
    if (next === (bot.welcomeMessage ?? '')) return;
    update.mutate({ id: bot.id, welcomeMessage: next }, { onSuccess: onSaved, onError });
  };

  return (
    <div className="px-[18px] pb-5 pt-3.5">
      <p className="mb-3 text-xs text-[var(--iris-muted)]">
        Starts from the Notifi template. Edit the parts that are yours — the rest is generated or
        filled in for you.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onBlur={commitDisplayName}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            maxLength={80}
            aria-label="Bot name"
            className={cn(irisField, 'w-[170px] font-medium')}
          />
          <span className="rounded-md bg-[var(--iris-line)] px-2 py-1.5 font-mono text-[11px] text-[var(--iris-body)]">
            {bot.name}
          </span>
          {bot.isEntrypoint ? (
            <Eyebrow>Entry point</Eyebrow>
          ) : (
            <IrisLinkButton
              onClick={() =>
                update.mutate(
                  { id: bot.id, isEntrypoint: true },
                  {
                    onSuccess: () => toast.success(`“${bot.displayName}” is now the entry point`),
                    onError,
                  },
                )
              }
            >
              Make entry point
            </IrisLinkButton>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div
            className="flex items-center rounded-md border border-[var(--iris-rule)] p-0.5"
            role="group"
            aria-label="Bot mode"
          >
            {(['PROMPT', 'FLOW'] as const).map((m) => {
              const active = (m === 'FLOW') === isFlow;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() =>
                    m !== bot.mode &&
                    update.mutate(
                      { id: bot.id, mode: m },
                      {
                        onSuccess: () =>
                          toast.success(
                            m === 'FLOW' ? 'Switched to Flow mode' : 'Switched to Prompt mode',
                          ),
                        onError,
                      },
                    )
                  }
                  className={cn(
                    'flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                    active
                      ? 'bg-[var(--iris-ink)] text-[var(--iris-on-ink)]'
                      : 'text-[var(--iris-muted)] hover:text-[var(--iris-ink)]',
                  )}
                >
                  {m === 'FLOW' ? <Waypoints className="size-3" /> : <Bot className="size-3" />}
                  {m === 'FLOW' ? 'Flow' : 'Prompt'}
                </button>
              );
            })}
          </div>
          <IrisToggle
            checked={bot.enabled}
            label={`${bot.displayName} enabled`}
            onChange={(on) => update.mutate({ id: bot.id, enabled: on }, { onError })}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--iris-muted)]">
        <span>Auto-included</span>
        {['Services', 'Areas', 'Hours', 'Booking tools', 'Knowledge'].map((s) => (
          <span key={s} className="flex items-center gap-2">
            <span className="text-[var(--iris-ghost)]" aria-hidden>
              ·
            </span>
            {s}
          </span>
        ))}
      </div>

      {isFlow ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-[var(--iris-muted)]">
            Design the conversation as a step-by-step flow. Add nodes, connect them by dragging
            between handles, click a node to configure it, then Save.
          </p>
          <FlowBuilder bot={bot} otherBots={allBots.filter((b) => b.id !== bot.id)} />
        </div>
      ) : (
        <>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onBlur={commitPrompt}
            placeholder="Describe how this bot should greet customers and what it can help with."
            rows={12}
            maxLength={8000}
            aria-label="Instructions (system prompt)"
            className={cn(irisField, 'mt-3 font-mono text-xs leading-relaxed')}
          />

          <div className="mt-3.5 grid gap-3 sm:grid-cols-2">
            <div>
              <Eyebrow>Welcome message</Eyebrow>
              <textarea
                value={welcome}
                onChange={(e) => setWelcome(e.target.value)}
                onBlur={commitWelcome}
                placeholder="Sent first to any new customer. Leave blank for none."
                rows={3}
                maxLength={1600}
                aria-label="Welcome message"
                className={cn(irisField, 'mt-2 leading-relaxed')}
              />
            </div>
            <div className="rounded-lg border border-[var(--iris-hairline)] bg-[var(--iris-soft)] p-3">
              <Eyebrow>Preview · sms</Eyebrow>
              <div className="mt-2 rounded-xl rounded-bl-sm border border-[var(--iris-hairline)] bg-[var(--iris-surface)] px-3 py-2.5 text-xs leading-relaxed text-[var(--iris-ink)]">
                {welcome.trim() || (
                  <span className="text-[var(--iris-ghost)]">
                    No welcome message — Iris replies only once the customer writes first.
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** 04 — facts Iris can quote, and documents it searches at answer time. */
function KnowledgeStep() {
  const { data: entries = [], isLoading } = useKnowledge();
  const [view, setView] = useState<'facts' | 'documents'>('facts');
  const [composing, setComposing] = useState(false);

  return (
    <div className="px-[18px] pb-5 pt-3.5">
      <div className="flex w-fit gap-1 rounded-lg bg-[var(--iris-line)] p-0.5">
        {(['facts', 'documents'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={cn(
              'rounded-md px-2.5 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)]',
              view === v
                ? 'bg-[var(--iris-surface)] font-medium text-[var(--iris-ink)] shadow-sm'
                : 'text-[var(--iris-muted)] hover:text-[var(--iris-ink)]',
            )}
          >
            {v === 'facts' ? `Facts · ${entries.length}` : 'Documents'}
          </button>
        ))}
      </div>

      {view === 'facts' ? (
        <>
          <p className="mt-3 text-xs leading-relaxed text-[var(--iris-muted)]">
            Short facts Iris can quote — FAQs, policies, guarantees. Your services, service areas
            and hours already flow in automatically, so add what isn&rsquo;t modelled elsewhere.
          </p>
          {isLoading ? (
            <Skeleton className="mt-3 h-16 w-full rounded-lg" />
          ) : entries.length === 0 ? (
            <IrisEmpty className="mt-3" title="No facts yet">
              Add the answers your office repeats all day.
            </IrisEmpty>
          ) : (
            <div className="mt-3 flex flex-col gap-1.5">
              {entries.map((e) => (
                <KnowledgeFact key={e.id} entry={e} />
              ))}
            </div>
          )}
          {composing ? (
            <NewFactComposer onClose={() => setComposing(false)} />
          ) : (
            <IrisAddButton className="mt-2.5" onClick={() => setComposing(true)}>
              Add a fact
            </IrisAddButton>
          )}
        </>
      ) : (
        <div className="mt-3">
          <p className="mb-3 text-xs leading-relaxed text-[var(--iris-muted)]">
            Long articles Iris searches at answer time. Paste text or point at a URL.
          </p>
          <IrisKnowledgeSources />
        </div>
      )}
    </div>
  );
}

function KnowledgeFact({ entry }: { entry: KnowledgeEntry }) {
  const update = useUpdateKnowledge();
  const del = useDeleteKnowledge();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(entry.title);
  const [content, setContent] = useState(entry.content);

  const onError = (err: unknown) =>
    toast.error((err as { message?: string })?.message ?? 'Could not update entry');
  const onSaved = () => toast.success('Saved');

  const commitTitle = () => {
    const t = title.trim();
    if (!t || t === entry.title) {
      setTitle(entry.title);
      return;
    }
    update.mutate({ id: entry.id, title: t }, { onSuccess: onSaved, onError });
  };

  const commitContent = () => {
    const c = content.trim();
    if (c === entry.content) return;
    update.mutate({ id: entry.id, content: c }, { onSuccess: onSaved, onError });
  };

  const remove = () => {
    if (!window.confirm(`Delete knowledge entry “${entry.title}”?`)) return;
    del.mutate(entry.id, { onSuccess: () => toast.success('Knowledge deleted'), onError });
  };

  return (
    <IrisDisclosure
      open={open}
      onToggle={() => setOpen((v) => !v)}
      title={entry.title}
      meta={
        <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--iris-muted)]">
          {KNOWLEDGE_CATEGORY_LABELS[entry.category]}
        </span>
      }
      trailing={
        <div className="flex items-center gap-2">
          <IrisToggle
            checked={entry.enabled}
            label={`${entry.title} enabled`}
            onChange={(on) => update.mutate({ id: entry.id, enabled: on }, { onError })}
          />
          <IrisIconButton
            onClick={remove}
            disabled={del.isPending}
            aria-label={`Delete ${entry.title}`}
          >
            <Trash2 className="size-3.5" />
          </IrisIconButton>
        </div>
      }
    >
      <div className="space-y-2 pl-6">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          maxLength={200}
          aria-label="Title"
          className={cn(irisField, 'font-medium')}
        />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onBlur={commitContent}
          rows={4}
          maxLength={8000}
          aria-label="Content"
          className={cn(irisField, 'leading-relaxed')}
        />
      </div>
    </IrisDisclosure>
  );
}

function NewFactComposer({ onClose }: { onClose: () => void }) {
  const create = useCreateKnowledge();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<KnowledgeCategory>('faq');
  const [content, setContent] = useState('');

  const canSubmit = title.trim().length > 0 && content.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      { title: title.trim(), category, content: content.trim(), enabled: true },
      {
        onSuccess: () => {
          toast.success('Knowledge added');
          onClose();
        },
        onError: (err) =>
          toast.error((err as { message?: string })?.message ?? 'Could not add entry'),
      },
    );
  };

  return (
    <div className="mt-2.5 rounded-lg border border-dashed border-[var(--iris-rule-strong)] bg-[var(--iris-surface)] p-3.5">
      <Eyebrow>New fact</Eyebrow>
      <div className="mt-2.5 space-y-2.5">
        <div className="grid gap-2.5 sm:grid-cols-[minmax(0,1fr)_176px]">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Cancellation policy"
            maxLength={200}
            aria-label="Title"
            className={irisField}
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as KnowledgeCategory)}
            aria-label="Category"
            className={cn(irisField, 'cursor-pointer')}
          >
            {KNOWLEDGE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {KNOWLEDGE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="e.g. Cancellations within 24 hours of the appointment incur a $50 fee."
          rows={3}
          maxLength={8000}
          aria-label="Content"
          className={cn(irisField, 'leading-relaxed')}
        />
        <div className="flex items-center justify-end gap-3">
          <IrisLinkButton onClick={onClose}>Cancel</IrisLinkButton>
          <IrisButton onClick={submit} disabled={!canSubmit || create.isPending}>
            Add knowledge
          </IrisButton>
        </div>
      </div>
    </div>
  );
}

// ─── Ship ─────────────────────────────────────────────────────────────────────

/** Deploy — what must be true before real customers reach Iris. */
function DeployTab({ bots }: { bots: AiReceptionist[] }) {
  const entry = bots.find((b) => b.isEntrypoint) ?? null;
  const enabled = bots.filter((b) => b.enabled);

  /** Preflight reads only from what this page already knows about the bots. */
  const preflight: { name: string; note: string; state: 'done' | 'partial' | 'todo' }[] = [
    {
      name: 'At least one bot exists',
      note: `${bots.length} configured`,
      state: bots.length > 0 ? 'done' : 'todo',
    },
    {
      name: 'An entry point is set',
      note: entry ? entry.displayName : 'none set',
      state: entry ? 'done' : 'todo',
    },
    {
      name: 'The entry point is switched on',
      note: entry ? (entry.enabled ? 'On' : 'Off') : '—',
      state: entry?.enabled ? 'done' : 'todo',
    },
    {
      name: 'Bots are switched on',
      note: `${enabled.length} of ${bots.length} on`,
      state:
        bots.length === 0
          ? 'todo'
          : enabled.length === bots.length
            ? 'done'
            : enabled.length > 0
              ? 'partial'
              : 'todo',
    },
  ];

  return (
    <div className="flex flex-wrap items-start gap-3">
      <IrisPanel title="Preflight" className="min-w-0 flex-1 basis-[400px]">
        <div>
          {preflight.map((p) => (
            <div
              key={p.name}
              className="flex items-center gap-2.5 border-b border-[var(--iris-hairline)] py-2.5 last:border-0"
            >
              <IrisMark state={p.state} />
              <span className="min-w-0 flex-1 text-[13px] text-[var(--iris-ink)]">{p.name}</span>
              <span className="text-right text-xs text-[var(--iris-muted)]">{p.note}</span>
            </div>
          ))}
        </div>
      </IrisPanel>

      <IrisPanel title="Where Iris answers" className="min-w-0 flex-1 basis-[320px]">
        <p className="text-xs leading-relaxed text-[var(--iris-muted)]">
          Iris goes live by being assigned to a channel — an SMS number or an email inbox. A bot
          that is switched on still answers nobody until a channel points at it.
        </p>
        <div className="mt-3 flex flex-col gap-1.5">
          {bots.map((b) => (
            <div
              key={b.id}
              className="flex items-center gap-2.5 rounded-lg border border-[var(--iris-rule)] px-3 py-2.5"
            >
              <Bot className="size-[15px] shrink-0 text-[var(--iris-muted)]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-medium text-[var(--iris-ink)]">
                  {b.displayName}
                </p>
                <Mono className="mt-0.5 block">
                  {b.isEntrypoint ? 'entry point' : 'specialist'}
                </Mono>
              </div>
              <Chip tone={b.enabled ? 'good' : 'neutral'}>{b.enabled ? 'On' : 'Off'}</Chip>
            </div>
          ))}
        </div>
        <div className="mt-3.5 border-t border-[var(--iris-hairline)] pt-3">
          <Link
            to="/dashboard/settings/channels"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--iris-ink)] px-3 py-2 text-[13px] font-medium text-[var(--iris-on-ink)] hover:bg-[var(--iris-ink-hover)]"
          >
            Assign to channels <span aria-hidden>→</span>
          </Link>
        </div>
      </IrisPanel>
    </div>
  );
}

/** Test — the simulator plus the batch runs. */
function TestTab({ hasBots, onOpenSimulator }: { hasBots: boolean; onOpenSimulator: () => void }) {
  return (
    <div className="space-y-3">
      <IrisPanel title="Simulator · nothing here reaches a customer">
        <p className="text-xs leading-relaxed text-[var(--iris-muted)]">
          Chat with Iris exactly as a customer would over SMS. The router picks the bot that
          replies, and every reply can be opened to show what it used. No real messages are sent.
        </p>
        <IrisButton className="mt-3" onClick={onOpenSimulator} disabled={!hasBots}>
          <MessagesSquare className="size-4" />
          {hasBots ? 'Open the simulator' : 'Create a bot first'}
        </IrisButton>
      </IrisPanel>
      <IrisBatchTesting />
    </div>
  );
}

// ─── Customer simulator ───────────────────────────────────────────────────────

interface Turn {
  id: string;
  role: 'user' | 'bot';
  text: string;
  handledBy?: string | null;
  /** Conversation this reply belongs to (bot turns only) — keys answer inspection. */
  conversationId?: string;
  /** Ordinal of this reply among the conversation's AI turns — indexes the trace. */
  traceIndex?: number;
}

/** ai-operations returns a plain string in prod; the local OpenAI mock returns
 * JSON — pretty-print that so it stays legible in the transcript. */
function renderReply(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return raw;
    }
  }
  return raw;
}

function randomCustomerPhone(): string {
  // +1 (555) 01X-XXXX — a full 10-digit number in the reserved 555-01xx test range.
  const seven = `01${String(Math.floor(Math.random() * 100000)).padStart(5, '0')}`;
  return `+1555${seven}`;
}

function formatPhone(e164: string): string {
  const d = e164.replace(/\D/g, '').slice(-10);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Openers offered on an empty simulator, so testing starts in one click. */
const SIM_PROMPTS = [
  'can someone come out thursday',
  'how much is a new water heater',
  'do you cover my area',
];

function SimulatorPanel({ hasBots, onClose }: { hasBots: boolean; onClose: () => void }) {
  const test = useTestReceptionist();
  const resume = useResumeTestReceptionist();
  const qc = useQueryClient();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [handedOff, setHandedOff] = useState(false);
  const [handoffReason, setHandoffReason] = useState<ReceptionistChatReply['handoffReason']>(null);
  const [phone, setPhone] = useState(() => randomCustomerPhone());
  /** Display-only legend of who this number belongs to when impersonating an
   * existing customer. NEVER sent anywhere — the simulator hands Iris the raw
   * phone number only, so she performs the same CRM lookup she performs in
   * production instead of being told who the customer is. */
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const contactSearch = useContactSearch(contactQuery, 8, { enabled: pickerOpen });
  const seqRef = useRef(0);
  /** Ordinal of the next successful AI reply — lines each bubble up with its trace. */
  const traceCountRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, test.isPending]);

  const nextId = () => String(seqRef.current++);

  const sendText = (message: string) => {
    if (!message || test.isPending) return;
    setTurns((t) => [...t, { id: `u${nextId()}`, role: 'user', text: message }]);
    setInput('');
    test.mutate(
      { message, conversationId, customerPhone: phone, channel: 'sms' },
      {
        onSuccess: (reply) => {
          setConversationId(reply.conversationId);
          setHandedOff((current) => current || reply.escalated || Boolean(reply.handoffReason));
          if (reply.handoffReason) setHandoffReason(reply.handoffReason);
          setTurns((turns) => {
            const additions: Turn[] = [];
            if (reply.replyText) {
              additions.push({
                id: `b${nextId()}`,
                role: 'bot',
                text: renderReply(reply.replyText),
                handledBy: reply.handledByRole,
                conversationId: reply.conversationId,
                traceIndex: traceCountRef.current++,
              });
            }
            return additions.length ? [...turns, ...additions] : turns;
          });
          // The turn's inspection trace is written best-effort after the reply;
          // drop any cached traces so opening "Why this answer" refetches fresh.
          void qc.invalidateQueries({ queryKey: conversationTracesKey(reply.conversationId) });
        },
        onError: (err) => {
          setTurns((t) => [
            ...t,
            {
              id: `e${nextId()}`,
              role: 'bot',
              text: (err as { message?: string })?.message ?? 'The bot engine did not respond.',
            },
          ]);
        },
      },
    );
  };

  const resetConversation = () => {
    setTurns([]);
    setConversationId(undefined);
    setHandedOff(false);
    setHandoffReason(null);
    traceCountRef.current = 0;
  };

  const newCustomer = () => {
    resetConversation();
    setPhone(randomCustomerPhone());
    setImpersonating(null);
  };

  /** Test the RETURNING-customer path: adopt an existing contact's number.
   * Only the number crosses the wire — Iris looks the customer up herself. */
  const impersonateNumber = (contactPhone: string, name: string) => {
    resetConversation();
    setPhone(contactPhone);
    setImpersonating(name);
    setPickerOpen(false);
    setContactQuery('');
  };

  return (
    <div className="iris-studio fixed bottom-4 right-4 top-20 z-30 flex w-[var(--iris-sim-w)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-[var(--iris-rule)] bg-[var(--iris-surface)] shadow-2xl">
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-[var(--iris-hairline)] px-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="iris-orb size-6 shrink-0 rounded-full" />
          <div className="min-w-0 leading-tight">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-[var(--iris-muted)]">
              Simulator
            </p>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-1 font-mono text-[11px] text-[var(--iris-ink)] hover:underline"
                  aria-label="Choose the number to text from"
                >
                  <span className="truncate">{formatPhone(phone)}</span>
                  {impersonating ? (
                    <span className="truncate font-sans text-[10px] text-[var(--iris-muted)]">
                      · {impersonating}
                    </span>
                  ) : null}
                  <ChevronDown className="size-3 shrink-0 text-[var(--iris-muted)]" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-2">
                <p className="mb-1.5 text-[11px] text-[var(--iris-muted)]">
                  Text as an existing customer — their number is used, but Iris still has to
                  recognize them on her own.
                </p>
                <Input
                  autoFocus
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  placeholder="Search customers…"
                  className="mb-1.5 h-8 text-sm"
                />
                <div className="max-h-48 overflow-y-auto">
                  {contactQuery.trim().length < 2 ? (
                    <p className="px-1 py-1.5 text-[11px] text-[var(--iris-muted)]">
                      Type at least 2 characters to search.
                    </p>
                  ) : contactSearch.isFetching && !(contactSearch.data?.data ?? []).length ? (
                    <p className="px-1 py-1.5 text-[11px] text-[var(--iris-muted)]">Searching…</p>
                  ) : (contactSearch.data?.data ?? []).length === 0 ? (
                    <p className="px-1 py-1.5 text-[11px] text-[var(--iris-muted)]">
                      No customers matched.
                    </p>
                  ) : (
                    (contactSearch.data?.data ?? []).map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        disabled={!contact.primaryPhone}
                        onClick={() =>
                          contact.primaryPhone &&
                          impersonateNumber(contact.primaryPhone, contact.displayName)
                        }
                        className="flex w-full items-baseline justify-between gap-2 rounded px-1.5 py-1.5 text-left text-xs hover:bg-[var(--iris-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="truncate">{contact.displayName}</span>
                        <span className="shrink-0 font-mono text-[10.5px] text-[var(--iris-muted)]">
                          {contact.primaryPhone ?? 'no phone on file'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    newCustomer();
                    setPickerOpen(false);
                    setContactQuery('');
                  }}
                  className="mt-1.5 w-full rounded border border-[var(--iris-rule)] px-2 py-1.5 text-center text-[11px] text-[var(--iris-body)] hover:border-[var(--iris-ink)]"
                >
                  New customer (random number)
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <IrisIconButton
            onClick={newCustomer}
            disabled={turns.length === 0}
            aria-label="New customer"
          >
            <RefreshCw className="size-3.5" />
          </IrisIconButton>
          <IrisIconButton onClick={onClose} aria-label="Hide simulator">
            <X className="size-3.5" />
          </IrisIconButton>
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {turns.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <span className="iris-orb size-9 rounded-full opacity-80" />
            <p className="text-xs text-[var(--iris-muted)]">
              {hasBots
                ? 'Text as a customer — the router picks the bot that replies. Nothing here reaches anyone.'
                : 'Create a bot first, then text here to test it. Nothing here reaches anyone.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {turns.map((t) => (
              <Bubble key={t.id} turn={t} />
            ))}
            {test.isPending ? (
              <p className="text-xs text-[var(--iris-muted)]">
                <span className="animate-pulse">Thinking…</span>
              </p>
            ) : null}
            {handedOff ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <p className="font-medium">Iris paused for operator takeover.</p>
                <p className="mt-0.5 opacity-80">
                  New customer messages are recorded, but Iris stays silent while an operator owns
                  the thread. Start a new customer to test Iris again.
                </p>
                {handoffReason ? (
                  <div className="mt-2 border-t border-amber-300/70 pt-2 dark:border-amber-800/70">
                    <p>
                      <span className="font-medium">Triggered by:</span>{' '}
                      {handoffReason.triggerType.replaceAll('_', ' ').toLowerCase()}
                    </p>
                    {handoffReason.triggerDetail ? (
                      <p className="mt-0.5 opacity-80">{handoffReason.triggerDetail}</p>
                    ) : null}
                  </div>
                ) : null}
                <IrisButton
                  className="mt-2"
                  disabled={!conversationId || resume.isPending}
                  onClick={() => {
                    if (!conversationId) return;
                    resume.mutate(conversationId, {
                      onSuccess: () => {
                        setHandedOff(false);
                        setHandoffReason(null);
                        toast.success('Iris resumed');
                      },
                      onError: (error) =>
                        toast.error(
                          (error as { message?: string })?.message ?? 'Could not resume Iris',
                        ),
                    });
                  }}
                >
                  {resume.isPending ? 'Resuming…' : 'Resume Iris'}
                </IrisButton>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {turns.length === 0 ? (
        <div className="flex flex-wrap gap-1.5 border-t border-[var(--iris-hairline)] px-3 py-2.5">
          {SIM_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => sendText(p)}
              disabled={!hasBots || test.isPending}
              className="rounded-full border border-[var(--iris-rule)] bg-[var(--iris-surface)] px-2.5 py-1 text-[11.5px] text-[var(--iris-body)] hover:border-[var(--iris-ink)] hover:text-[var(--iris-ink)] disabled:opacity-40"
            >
              {p}
            </button>
          ))}
        </div>
      ) : null}

      <div className="border-t border-[var(--iris-hairline)] p-3">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendText(input.trim());
              }
            }}
            placeholder="Text as the customer…"
            rows={1}
            className="max-h-32 min-h-[40px] flex-1 resize-none border-[var(--iris-rule)] bg-[var(--iris-surface)] text-[13px]"
            maxLength={1600}
          />
          <IrisButton
            onClick={() => sendText(input.trim())}
            disabled={!input.trim() || test.isPending}
            className="shrink-0 px-2.5 py-2.5"
            aria-label="Send"
          >
            <Send className="size-4" />
          </IrisButton>
        </div>
      </div>
    </div>
  );
}

function Bubble({ turn }: { turn: Turn }) {
  const isUser = turn.role === 'user';
  const [showInspect, setShowInspect] = useState(false);
  const inspect =
    !isUser && turn.conversationId && turn.traceIndex != null
      ? { conversationId: turn.conversationId, traceIndex: turn.traceIndex }
      : null;

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed',
          isUser
            ? 'rounded-br-sm bg-[var(--iris-ink)] text-[var(--iris-on-ink)]'
            : 'rounded-bl-sm border border-[var(--iris-hairline)] bg-[var(--iris-soft)] text-[var(--iris-ink)]',
        )}
      >
        <pre className="whitespace-pre-wrap break-words font-sans">{turn.text}</pre>
      </div>
      {!isUser && (turn.handledBy || inspect) ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-2">
          {turn.handledBy ? <Mono>{turn.handledBy}</Mono> : null}
          {inspect ? (
            <button
              type="button"
              onClick={() => setShowInspect((v) => !v)}
              aria-expanded={showInspect}
              className="inline-flex items-center gap-1 text-[11.5px] text-[var(--iris-muted)] underline underline-offset-2 hover:text-[var(--iris-ink)]"
            >
              <Search className="size-3" />
              Why this answer
              <ChevronDown
                className={cn('size-3 transition-transform', showInspect && 'rotate-180')}
              />
            </button>
          ) : null}
        </div>
      ) : null}
      {inspect && showInspect ? (
        <SimulatorTurnInspection
          conversationId={inspect.conversationId}
          traceIndex={inspect.traceIndex}
        />
      ) : null}
    </div>
  );
}

/**
 * Compact per-reply "Why this answer" inspection for the simulator. The chat
 * response only carries the reply text + handling role, so grounding/sources
 * come from the Answer-Inspection trace persisted for the conversation (same
 * data as the Test/Conversations inspection panel), selected by turn ordinal.
 */
function SimulatorTurnInspection({
  conversationId,
  traceIndex,
}: {
  conversationId: string;
  traceIndex: number;
}) {
  const { data, isLoading, isError } = useConversationTraces(conversationId);
  const trace = data?.turns[traceIndex];

  if (isLoading) {
    return (
      <div className="mt-1.5 w-full rounded-lg border border-[var(--iris-rule)] bg-[var(--iris-hover)] p-2.5">
        <Skeleton className="h-10 w-full rounded" />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="mt-1.5 text-[11px] text-[var(--iris-muted)]">
        Couldn&rsquo;t load the inspection for this reply.
      </p>
    );
  }
  if (!trace) {
    return (
      <p className="mt-1.5 text-[11px] italic text-[var(--iris-muted)]">
        Inspection for this reply isn&rsquo;t ready yet — it&rsquo;s recorded a moment after the
        reply. Reopen in a few seconds.
      </p>
    );
  }
  return <TurnInspectionBody trace={trace} />;
}

/** Shared "why this answer" body — also rendered by the Inbox thread view. */
export function TurnInspectionBody({ trace }: { trace: TurnTrace }) {
  return (
    <div className="mt-1.5 w-full max-w-[440px] space-y-2 rounded-lg border border-[var(--iris-rule)] bg-[var(--iris-hover)] p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <GroundedBadge grounded={trace.grounded} />
        {trace.confidence != null ? (
          <Mono className="inline-flex items-center gap-1">
            <Gauge className="size-2.5" />
            {Math.round(trace.confidence * 100)}%
          </Mono>
        ) : null}
        {trace.handledByRole ? (
          <Mono className="inline-flex items-center gap-1">
            <UserCog className="size-2.5" />
            {trace.handledByRole}
          </Mono>
        ) : null}
      </div>

      {trace.reasoning ? (
        <p className="text-[11px] italic text-[var(--iris-muted)]">{trace.reasoning}</p>
      ) : null}

      <InspectSection icon={BookOpen} title="Retrieved sources" count={trace.sources.length}>
        {trace.sources.length === 0 ? (
          <InspectEmpty>No knowledge was retrieved for this answer.</InspectEmpty>
        ) : (
          <div className="space-y-1">
            {trace.sources.map((s, i) => (
              <InspectSourceRow key={i} source={s} />
            ))}
          </div>
        )}
      </InspectSection>

      {trace.guidance.length > 0 ? (
        <InspectSection icon={Compass} title="Guidance applied" count={trace.guidance.length}>
          <ul className="space-y-1">
            {trace.guidance.map((g, i) => (
              <li
                key={i}
                className="rounded border border-[var(--iris-rule)] bg-[var(--iris-surface)] px-2 py-1 text-[11px] text-[var(--iris-body)]"
              >
                {g.label ? (
                  <span className="mr-1 font-mono uppercase text-[var(--iris-muted)]">
                    {g.label}
                  </span>
                ) : null}
                {g.text}
              </li>
            ))}
          </ul>
        </InspectSection>
      ) : null}

      {trace.tools.length > 0 ? (
        <InspectSection icon={Wrench} title="Tools called" count={trace.tools.length}>
          <ul className="flex flex-wrap gap-1">
            {trace.tools.map((t, i) => (
              <li
                key={i}
                className={cn(
                  'rounded-full border px-2 py-0.5 font-mono text-[10px]',
                  t.isError
                    ? 'border-[var(--iris-warn-fg)]/40 bg-[var(--iris-warn-bg)] text-[var(--iris-warn-fg)]'
                    : 'border-[var(--iris-rule)] text-[var(--iris-muted)]',
                )}
              >
                {t.name}
                {t.isError ? ' · failed' : ''}
              </li>
            ))}
          </ul>
          {/* The failure reason is the whole diagnosis — a red pill without it
              still forces a trip to the full inspection panel. */}
          {trace.tools.some((t) => t.isError) ? (
            <ul className="space-y-1">
              {trace.tools
                .filter((t) => t.isError)
                .map((t, i) => (
                  <li
                    key={i}
                    className="rounded border border-[var(--iris-warn-fg)]/30 bg-[var(--iris-warn-bg)] px-2 py-1 text-[10px] leading-relaxed text-[var(--iris-warn-fg)]"
                  >
                    <span className="font-mono">{t.name}</span>: {toolErrorText(t.result)}
                  </li>
                ))}
            </ul>
          ) : null}
        </InspectSection>
      ) : null}
    </div>
  );
}

function InspectSection({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof BookOpen;
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--iris-muted)]">
        <Icon className="size-3" />
        {title}
        <span className="tabular-nums opacity-70">· {count}</span>
      </div>
      {children}
    </div>
  );
}

function InspectSourceRow({ source }: { source: TraceSource }) {
  return (
    <div className="rounded border border-[var(--iris-rule)] bg-[var(--iris-surface)] p-1.5">
      <div className="flex items-start justify-between gap-1.5">
        <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-[var(--iris-ink)]">
          {source.title ?? 'Untitled snippet'}
        </p>
        {source.score != null ? (
          <MonoChip className="shrink-0 px-1.5 py-0.5 text-[9px]">
            {(source.score * 100).toFixed(0)}%
          </MonoChip>
        ) : null}
      </div>
      <p className="mt-0.5 line-clamp-3 text-[10px] text-[var(--iris-muted)]">{source.text}</p>
    </div>
  );
}

function InspectEmpty({ children }: { children: ReactNode }) {
  return <p className="text-[10px] italic text-[var(--iris-muted)]">{children}</p>;
}

/** Pull the human-readable error/message out of a failed tool's JSON result. */
function toolErrorText(result: unknown): string {
  const text = typeof result === 'string' ? result : JSON.stringify(result ?? '');
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const detail = parsed.error ?? parsed.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
  } catch {
    // Truncated trace payload — fall through to the raw text.
  }
  return text.slice(0, 240) || 'failed';
}

export function GroundedBadge({ grounded }: { grounded: boolean }) {
  return (
    <Chip tone={grounded ? 'good' : 'note'}>
      {grounded ? <Lock className="size-2.5" /> : <BookOpen className="size-2.5" />}
      {grounded ? 'Grounded' : 'Ungrounded'}
    </Chip>
  );
}
